import React from 'react';
import Banner from '../common/Banner';
import type { AuthUser } from '../../types/permissions';
import { DEFAULT_AGENCY_TIMEZONE } from '../../types/permissions';
import { canSignBaa } from '../../utils/baaAccess';

interface BaaStatusBannerProps {
  user: AuthUser | null;
  /** Opens the BAA signing page (shown to owners and admins only). */
  onReview: () => void;
}

/** Format an ISO date as "October 31, 2026" in the agency timezone. */
export function formatGraceDate(iso: string | null | undefined, timeZone: string = DEFAULT_AGENCY_TIMEZONE): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
  try {
    return new Intl.DateTimeFormat('en-US', { ...options, timeZone }).format(date);
  } catch {
    return new Intl.DateTimeFormat('en-US', { ...options, timeZone: DEFAULT_AGENCY_TIMEZONE }).format(date);
  }
}

/**
 * BAA version notice for the main layout (Phase 5, ACC-04).
 * - grace: warning for all staff; owners/admins get "Review and sign".
 * - expired: error for staff who cannot sign (owners/admins are sent to the signing page instead).
 */
const BaaStatusBanner: React.FC<BaaStatusBannerProps> = ({ user, onReview }) => {
  if (!user) return null;
  const signer = canSignBaa(user);

  if (user.baaStatus === 'grace') {
    const date = formatGraceDate(user.baaGraceEndsAt, user.agencyTimezone);
    return (
      <Banner
        variant="warning"
        data-testid="baa-grace-banner"
        className="rounded-none border-x-0 border-t-0"
        action={signer ? (
          <button
            type="button"
            onClick={onReview}
            className="rounded bg-amber-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-800"
          >
            Review and sign
          </button>
        ) : undefined}
      >
        A new Business Associate Agreement version is available.{' '}
        {date ? `Sign it by ${date} to keep editing patient records.` : 'Sign it soon to keep editing patient records.'}
      </Banner>
    );
  }

  if (user.baaStatus === 'expired' && !signer) {
    return (
      <Banner variant="error" data-testid="baa-expired-banner" className="rounded-none border-x-0 border-t-0">
        Patient record editing is paused until an owner or admin signs the updated BAA.
      </Banner>
    );
  }

  return null;
};

export default BaaStatusBanner;
